import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  Heart,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useEffect, useRef } from "react";
import { formatCurrency } from "./shared";

export function ContributionSuccess({ walletId }: { walletId: string }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const searchParams = new URLSearchParams(window.location.search);
  const sessionId = searchParams.get("session_id");

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        'POST',
        `/api/condition-wallets/${walletId}/contribute/verify`,
        { sessionId }
      );
      return response.json();
    },
    onError: (error: any) => {
      toast({
        title: "Verification Issue",
        description: error.message || "We couldn't verify your payment right now, but don't worry — your contribution is safe.",
        variant: "destructive",
      });
    },
  });

  const verifyAttempted = useRef(false);
  useEffect(() => {
    if (sessionId && !verifyAttempted.current) {
      verifyAttempted.current = true;
      verifyMutation.mutate();
    }
  }, [sessionId]);

  const wallet = verifyMutation.data?.wallet;
  const contribution = verifyMutation.data?.contribution;

  if (!sessionId) {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <Card className="p-6 sm:p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold mb-2" data-testid="text-missing-session">Invalid Link</h1>
          <p className="text-muted-foreground mb-6">
            This page is reached after completing a payment. If you made a contribution, it has been recorded safely.
          </p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => setLocation(`/condition-wallets/${walletId}`)} data-testid="button-back-wallet">
              View Fund
            </Button>
            <Button variant="outline" onClick={() => setLocation('/conditions')} data-testid="button-browse-conditions">
              Browse All Funds
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLocation('/conditions')}
          data-testid="button-back-conditions"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          All Conditions
        </Button>
      </div>

      <Card className="p-6 sm:p-8 text-center">
        {verifyMutation.isPending ? (
          <div className="space-y-4">
            <Skeleton className="h-16 w-16 rounded-full mx-auto" />
            <Skeleton className="h-6 w-48 mx-auto" />
            <Skeleton className="h-4 w-64 mx-auto" />
          </div>
        ) : verifyMutation.isError ? (
          <div className="space-y-4">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold" data-testid="text-error-title">Something Went Wrong</h1>
            <p className="text-muted-foreground">
              Your payment may still have been processed. If you were charged, your contribution is safe and will be applied.
            </p>
            <div className="flex flex-col gap-2 pt-4">
              <Button onClick={() => setLocation(`/condition-wallets/${walletId}`)} data-testid="button-back-wallet">
                Back to Fund
              </Button>
              <Button variant="outline" onClick={() => setLocation('/conditions')} data-testid="button-browse-conditions">
                Browse All Funds
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold mb-2" data-testid="text-success-title">Thank You!</h1>
              <p className="text-lg text-muted-foreground">
                Your contribution makes a real difference for patients in our community.
              </p>
            </div>

            {contribution && (
              <Card className="p-4 bg-muted/30">
                <div className="space-y-2">
                  <div className="flex justify-between gap-2">
                    <span className="text-sm text-muted-foreground">Amount</span>
                    <span className="font-semibold" data-testid="text-contribution-amount">
                      {formatCurrency(contribution.amount)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-sm text-muted-foreground">Contributor</span>
                    <span className="font-medium" data-testid="text-contributor-name">
                      {contribution.displayName}
                    </span>
                  </div>
                  {wallet && (
                    <div className="flex justify-between gap-2">
                      <span className="text-sm text-muted-foreground">Fund</span>
                      <span className="font-medium" data-testid="text-fund-name">
                        {wallet.title}
                      </span>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {wallet && (
              <div className="pt-2">
                <p className="text-sm text-muted-foreground mb-2">Fund Progress</p>
                <Progress value={wallet.fundingGoal > 0 ? Math.round((wallet.totalRaised / wallet.fundingGoal) * 100) : 0} className="h-3" />
                <div className="flex justify-between gap-2 text-sm text-muted-foreground mt-1">
                  <span>{formatCurrency(wallet.totalRaised)} raised</span>
                  <span>Goal: {formatCurrency(wallet.fundingGoal)}</span>
                </div>
              </div>
            )}

            <Separator />

            <div className="space-y-3">
              <p className="text-sm font-medium">Share & Inspire Others</p>
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const text = `I just contributed to ${wallet?.title || 'a community health fund'} on HealthWallet! Join me in supporting healthcare access.`;
                    const url = `${window.location.origin}/condition-wallets/${walletId}`;
                    if (navigator.share) {
                      navigator.share({ title: "HealthWallet Contribution", text, url });
                    } else {
                      navigator.clipboard.writeText(`${text} ${url}`);
                      toast({ title: "Link Copied", description: "Share link copied to clipboard!" });
                    }
                  }}
                  data-testid="button-share"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button onClick={() => setLocation(`/condition-wallets/${walletId}`)} data-testid="button-back-wallet">
                Back to Fund
              </Button>
              <Button variant="outline" onClick={() => setLocation('/conditions')} data-testid="button-browse-conditions">
                Explore More Funds
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
